// WHOSE DAY IS IT? (rewritten 2026-09-26)
//
// Caleb, on the sign list the morning of the 26th: "it said Cheryl Bean's signs were put up on 9/25
// today. we should be on central time, but i think its gmt. We have a timezone for the campaign in
// settings."
//
// Two separate bugs, and neither one was GMT.
//
// ── 1. AN AGE IN HOURS IS NOT AN AGE IN DAYS ──────────────────────────────────────────────────
//
// Every "how long ago" on the dashboard was elapsed milliseconds over 86,400,000, floored. Those
// signs went up at 11:12 on the 25th; read at 09:00 on the 26th they are 21.8 hours old, which
// floors to 0, which prints "today". So the row said "Sep 25 · today" -- the date right, the age
// wrong, and the two contradicting each other on one line.
//
// 21 hours ago is YESTERDAY. "Days ago" is a question about the calendar, not about duration, and
// the answer changes at midnight rather than 24 hours after whatever time the thing happened. The
// same arithmetic decided which signs are stale at 30 days, so that was up to a day out too.
//
// ── 2. THE DAY BELONGED TO THE VIEWER'S LAPTOP ────────────────────────────────────────────────
//
// `new Date(s).toLocaleDateString()` renders in whatever timezone the browser is set to. For Caleb
// in San Antonio that is Central, so it was right by luck -- and it would have been wrong for a
// manager checking a Texas campaign from a laptop on Eastern, where anything recorded after 11pm
// Central shows up on the following day. campaign.timezone has existed since 0042 for exactly this
// ("root cause of a 9 AM knock displaying as 04:00") and almost nothing used it: three call sites
// against 139 raw toLocale* calls.
//
// A campaign's day is the day where the campaign is. Not the server's, not the viewer's.
//
// ── WHY IT IS ALL HERE, AND WHY IT TAKES tz FIRST ─────────────────────────────────────────────
//
// Every function takes the timezone as its first argument and none of them reads a default. A
// helper that falls back to the browser when you forget is a helper that hides this bug again --
// the failure would be silent and correct-looking on the machine of whoever wrote it. The call
// site has `useCampaign()` in scope; it can pass the zone.

/** A local calendar day as YYYY-MM-DD, from the parts rather than from toISOString() -- whose UTC
 *  date rolls over early each evening in a Central-time campaign. */
export function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** en-CA formats as YYYY-MM-DD, which is the one thing every browser agrees on and the only reason
 *  that locale appears in this file. */
function ymd(tz: string, d: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(d);
}

/** Today's calendar day in `tz` (or the viewer's, when tz is null -- the platform screens that have
 *  no campaign in scope). */
export function todayLocal(tz?: string | null): string {
  return tz ? ymd(tz, new Date()) : fmtLocal(new Date());
}

/** The calendar day an instant falls on, in `tz`. Null in, null out. */
export function dayIn(tz: string | null | undefined, iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return tz ? ymd(tz, d) : fmtLocal(d);
}

/**
 * WHOLE CALENDAR DAYS between two instants, counted in `tz`. This is the function the old
 * elapsed-milliseconds arithmetic should have been.
 *
 * Both instants are reduced to their calendar day in the zone FIRST, then subtracted as dates. That
 * ordering is what makes 23:59 and 00:01 one day apart rather than zero, and it is also why DST is
 * not a problem: the subtraction happens between two UTC midnights that stand for local days, so a
 * 23-hour spring day and a 25-hour autumn day both count as one.
 */
export function daysApart(tz: string | null | undefined,
                          iso: string | null | undefined,
                          from?: Date): number | null {
  const a = dayIn(tz, iso);
  if (!a) return null;
  const b = tz ? ymd(tz, from ?? new Date()) : fmtLocal(from ?? new Date());
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

/**
 * Format an INSTANT in the campaign's zone, with whatever Intl options the screen wants.
 *
 * The named wrappers below cover the shapes this app actually uses; this is here so a call site
 * that needs a different one does not have to reach for `new Date(...).toLocaleString()` and
 * silently go back to rendering in the viewer's timezone.
 */
export function fmtWhen(tz: string | null | undefined, iso: string | null | undefined,
                        opts: Intl.DateTimeFormatOptions, dash = "—"): string {
  if (!iso) return dash;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return dash;
  return new Intl.DateTimeFormat(undefined, { ...opts, ...(tz ? { timeZone: tz } : {}) }).format(d);
}

/**
 * Format a CALENDAR DAY STRING -- "2026-09-25", the shape the server's day buckets and a date
 * input both produce. NO TIMEZONE, deliberately: a day is not an instant, it has already been
 * reduced, and applying a zone to it would shift it onto the day before or after.
 *
 * This replaces `new Date(d + "T00:00:00").toLocaleDateString()`, which got the right answer by
 * cancellation -- parsed as local midnight, rendered in local -- and looked exactly like the bug
 * that reads an instant in the wrong zone. Parsed as UTC and rendered in UTC here, so the two
 * halves cancel by construction rather than by luck.
 */
export function fmtYmd(ymd: string | null | undefined,
                       opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
                       dash = "—"): string {
  if (!ymd) return dash;
  const t = Date.parse(`${ymd}T00:00:00Z`);
  if (isNaN(t)) return dash;
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: "UTC" }).format(new Date(t));
}

/** "9/25/2026" -- the bare toLocaleDateString() shape, in the campaign's zone. */
export function fmtDate(tz: string | null | undefined, iso: string | null | undefined,
                        dash = "—"): string {
  return fmtWhen(tz, iso, {}, dash);
}

/** "2:05 PM", in the campaign's zone. */
export function fmtTime(tz: string | null | undefined, iso: string | null | undefined,
                        dash = "—"): string {
  return fmtWhen(tz, iso, { hour: "numeric", minute: "2-digit" }, dash);
}

/** "Sep 25", in the campaign's zone. */
export function fmtDay(tz: string | null | undefined, iso: string | null | undefined,
                       dash = "—"): string {
  if (!iso) return dash;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return dash;
  return new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", ...(tz ? { timeZone: tz } : {}),
  }).format(d);
}

/** "Sep 25, 2026, 11:12 AM", in the campaign's zone -- the long form for a tooltip. */
export function fmtDateTime(tz: string | null | undefined, iso: string | null | undefined,
                            dash = "—"): string {
  if (!iso) return dash;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return dash;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium", timeStyle: "short", ...(tz ? { timeZone: tz } : {}),
  }).format(d);
}

/**
 * "today" / "1d" / "3w", counted in calendar days in `tz`.
 *
 * NEGATIVE IS "today", NOT "-2d". A clock skewed a few minutes ahead on a phone that recorded a
 * knock should read as now, not as the future.
 */
export function agoLabel(tz: string | null | undefined, iso: string | null | undefined,
                         dash = "—", from?: Date): string {
  // `from` EXISTS SO THIS CAN BE TESTED. daysApart has always taken it; agoLabel did not forward
  // it, so every assertion about a label had to be written against the real wall clock -- and
  // dashboard/test/local-date.test.mjs duly went red the morning after it was written, because
  // "2026-09-26T15:00:00Z is today" stopped being true on the 27th. A test that only passes on
  // the day it was written is not a test, and worse, it goes red on somebody ELSE'S pull request
  // for a reason that has nothing to do with them.
  //
  // Production behaviour is unchanged: omitted, it is still now.
  const d = daysApart(tz, iso, from);
  if (d === null) return dash;
  if (d <= 0) return "today";
  if (d === 1) return "1d";
  if (d < 14) return `${d}d`;
  if (d < 60) return `${Math.floor(d / 7)}w`;
  return `${Math.floor(d / 30)}mo`;
}

/**
 * Whole days between two YYYY-MM-DD strings.
 *
 * Both are already calendar days, so there is no zone to apply and nothing to reduce -- but the
 * obvious way to write it, `(Date.parse(b) - Date.parse(a)) / 864e5`, is correct and
 * INDISTINGUISHABLE ON SIGHT from the bug this file exists to kill. It lives here, named, so a
 * reader does not have to work out which of the two they are looking at, and so the guard has one
 * fewer false positive to teach people to ignore.
 */
export function daysBetweenDays(a: string, b: string): number | null {
  const x = Date.parse(`${a}T00:00:00Z`), y = Date.parse(`${b}T00:00:00Z`);
  if (isNaN(x) || isNaN(y)) return null;
  return Math.round((y - x) / 86400000);
}
